import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '../../integrations/supabase/client';
import AutocompleteInput from '../AutocompleteInput';
const OnboardingStep3 = ({
  onComplete,
  userData,
  setUserData
}) => {
  const [isNewcomer, setIsNewcomer] = useState(userData.isNewcomer || null);
  const [arrivalMonth, setArrivalMonth] = useState(userData.arrivalMonth || '');
  const [originCity, setOriginCity] = useState(userData.originCity || '');
  const [originCoordinates, setOriginCoordinates] = useState(userData.originCoordinates || null);

  // Ensure originCity is never undefined
  useEffect(() => {
    if (originCity === undefined) {
      setOriginCity('');
    }
  }, [originCity]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const handleOriginLocationSelect = locationData => {
    console.log('🏙️ Origin city selected:', locationData);
    setOriginCity(locationData.location || locationData.name);
    setOriginCoordinates(locationData.coordinates);

    // Update userData to ensure it's available for the final submission
    setUserData(prev => ({
      ...prev,
      originCity: locationData.location || locationData.name,
      originCoordinates: locationData.coordinates
    }));
  };
  const handleComplete = async () => {
    const finalData = {
      ...userData,
      isNewcomer,
      arrivalMonth: isNewcomer ? arrivalMonth : null,
      originCity: isNewcomer ? originCity : null
    };
    setUserData(finalData);

    // Note: Köppen climate data is now handled automatically in completeOnboarding
    await onComplete(finalData);
  };

  // Generate month options for the current and previous year
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    // Add months from current year and previous year
    for (let year = currentYear; year >= currentYear - 1; year--) {
      for (let month = 11; month >= 0; month--) {
        const date = new Date(year, month, 1);
        const monthKey = date.toISOString().slice(0, 7);
        const monthName = date.toLocaleDateString('en', {
          month: 'long',
          year: 'numeric'
        });

        // Only add months that are in the past or current month
        if (date <= currentDate) {
          options.push({
            value: monthKey,
            label: monthName
          });
        }
      }
    }
    return options;
  };
  const monthOptions = generateMonthOptions();

  // Debug validation state - handle undefined values
  const safeOriginCity = originCity || '';
  const safeArrivalMonth = arrivalMonth || '';

  // Fix validation logic
  let isValidForSubmission = false;
  if (isNewcomer === null) {
    isValidForSubmission = false; // Must select yes/no first
  } else if (isNewcomer === false) {
    isValidForSubmission = true; // If not newcomer, only need to select this option
  } else if (isNewcomer === true) {
    isValidForSubmission = safeArrivalMonth.length > 0 && safeOriginCity.length > 0; // If newcomer, need both fields
  }
  return <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-gray-200 dark:border-gray-700">
      <div className="text-center mb-6">
        <h2 className="text-h2 text-foreground mb-2">
          Climate Adaptation
        </h2>
        <p className="text-body-sm text-muted-foreground">
          Have you moved to <span className="font-medium">{userData.location}</span> recently?
        </p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-3">
          <button onClick={() => setIsNewcomer(false)} className={`w-full p-4 rounded-lg border-2 transition-all group hover:scale-[1.02] ${isNewcomer === false ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
            <div className="flex items-center">
              <span className="text-2xl mr-3">🏠</span>
              <div className="flex-1 text-left">
                <div className="text-label text-foreground">
                  No, I've lived here for a while
                </div>
                <div className="text-body-sm text-muted-foreground mt-1">
                  I'm used to the local climate
                </div>
              </div>
              {isNewcomer === false && <div className="text-primary ml-2">
                  ✓
                </div>}
            </div>
          </button>
          
          <button onClick={() => setIsNewcomer(true)} className={`w-full p-4 rounded-lg border-2 transition-all group hover:scale-[1.02] ${isNewcomer === true ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-lg' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
            <div className="flex items-center">
              <span className="text-2xl mr-3">✈️</span>
              <div className="flex-1 text-left">
                <div className="text-label text-foreground">
                  Yes, I moved here recently
                </div>
                <div className="text-body-sm text-muted-foreground mt-1">
                  I'm still adapting to the climate
                </div>
              </div>
              {isNewcomer === true && <div className="text-primary ml-2">
                  ✓
                </div>}
            </div>
          </button>
        </div>

        {isNewcomer && <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 space-y-4">
            <div>
              <label className="block text-label text-foreground mb-3">
                Where did you move from?
              </label>
              
              {/* Show current selection if one exists */}
              {originCity && <div className="mb-2 p-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center text-body-sm text-green-700 dark:text-green-300">
                    <span className="mr-2">📍</span>
                    <span className="flex-1">{originCity}</span>
                    <button onClick={() => {
                setOriginCity('');
                setOriginCoordinates(null);
              }} className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200 ml-2">
                      ✕
                    </button>
                  </div>
                </div>}
              
              {/* Autocomplete input for origin city */}
              {!originCity && <div className="relative">
                  <AutocompleteInput onLocationSelect={handleOriginLocationSelect} theme="light" placeholder="Search for your previous city (e.g., Mumbai, New York)" />
                </div>}
              
              <p className="text-caption text-muted-foreground mt-2">
                This helps us understand your previous climate for better adaptation
              </p>
            </div>
            
            <div>
              <label className="block text-label text-foreground mb-3">
                What month did you move here?
              </label>
              <select value={arrivalMonth} onChange={e => setArrivalMonth(e.target.value)} className="w-full p-3 rounded-lg border border-input 
                         bg-background text-foreground
                         focus:ring-2 focus:ring-ring focus:border-ring 
                         transition-colors duration-200">
                <option value="">Select month</option>
                {monthOptions.map(option => <option key={option.value} value={option.value}>
                    {option.label}
                  </option>)}
              </select>
              {arrivalMonth && <p className="text-caption text-muted-foreground mt-2">
                  We'll adjust recommendations to help you adapt to the local climate
                </p>}
            </div>
          </div>}

        <button onClick={handleComplete} disabled={!isValidForSubmission} className="w-full mt-6 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium py-3 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-full">
          Complete Setup ✨
        </button>
      </div>
    </div>;
};
export default OnboardingStep3;